import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AnchorInfo, CodeBlockRedlineItem, UUID } from '@aline/common';
import { Select } from '@aline/common';
import { link } from '@aline/routes';
import {
  alpha,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from '@mui/material';
import confetti from 'canvas-confetti';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronsDownUp,
  ChevronsUpDown,
  CircleStop,
  MoreHorizontal,
  Pencil,
  Play,
  RefreshCw,
} from 'lucide-react';
import { AlineTooltip, PanelHeader, RouterLink, Spacer } from 'src/components';
import type { Apollo } from 'src/gql/generated';
import { openLinkFromAnchorClick, useDispatchInfoSnack } from 'src/helpers';
import { useGetCurrentDoc } from 'src/hooks/document';
import {
  useDocContentChangedCount,
  useEditorLoading,
  useMaybeEditorForDocument,
} from 'src/layout-engine';
import { createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { getPlaybookStrongBorder, PLAYBOOK_TONES } from './playbook-colors';
import { usePlaybookRun, usePlaybookRunActions } from './playbook-run-hooks';
import type {
  PlaybookCategoryLike,
  PlaybookRuleFilter,
  PlaybookRuleFilterCounts,
} from './playbook-run-state';
import {
  getAdjacentReviewQueueRuleId,
  getIsRuleInFilter,
  getPlaybookStatus,
  getReviewTargets,
  getRuleFilterCounts,
  getRuleJob,
  getShowPlaybookOutcomeFilters,
} from './playbook-run-state';
import {
  getIsLiveDocumentReadyToJudgeStaleness,
  getIsSuggestionStale,
  getLiveAnchorInfoByKey,
} from './playbook-staleness';
import { PlaybookCategoryCard } from './PlaybookCategoryCard';
import { PLAYBOOK_STATUS_CHIPS, PLAYBOOK_TONE_COLORS } from './PlaybookChip';
import { getPlaybookRuleElementId } from './PlaybookRuleCard';
import type { PlaybookRuleActions } from './PlaybookRuleCard';
import { reviewAcceptedSuggestion } from './review-accepted-suggestion';
import { usePlaybookAutoRerun } from './usePlaybookAutoRerun';
import { usePlaybookMaterialize } from './usePlaybookMaterialize';

const MENU_ICON_SIZE = 16;

const TITLE_MAX_LINES = 3;

const useStyles = makeStyles()(theme => {
  const hairline = `1px solid ${theme.appColors.rule}`;

  return {
    /**
     * Top hairline belongs to the list, not the first category: a first-child
     * border would duplicate this and break as soon as the empty-filter
     * message is the only child.
     */
    cards: {
      borderTop: hairline,
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      minHeight: 0,
    },
    /**
     * No top padding on the scroller itself: sticky offsets are measured from
     * its content box, so padding here would strand the stuck category header
     * that far down and let content scroll through the band above it. The
     * opening gap belongs to the first child instead.
     */
    container: {
      boxSizing: 'border-box',
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      overflowY: 'auto',
      padding: theme.spacing(0, 2, 2),
    },
    emptyFilter: {
      color: theme.appColors.secondaryType,
      fontSize: 14,
      padding: theme.spacing(3, 0),
      textAlign: 'center',
    },
    filter: {
      alignItems: 'center',
      background: theme.palette.common.white,
      border: hairline,
      borderRadius: 999,
      color: theme.appColors.secondaryType,
      cursor: 'pointer',
      display: 'flex',
      fontSize: 12,
      fontWeight: 600,
      gap: 4,
      minHeight: 26,
      padding: theme.spacing(0.25, 0.75),
      whiteSpace: 'nowrap',
    },
    filterActive: {
      backgroundColor: PLAYBOOK_TONES.running.background,
      borderColor: PLAYBOOK_TONES.running.border,
      color: PLAYBOOK_TONES.running.color,
    },
    filterCount: {
      fontVariantNumeric: 'tabular-nums',
      fontWeight: 600,
    },
    filterDot: {
      backgroundColor: PLAYBOOK_TONES.neutral.color,
      borderRadius: '50%',
      flexShrink: 0,
      height: 6,
      width: 6,
    },
    /** The queue counts what is left to review, so its dot carries that tone. */
    filterDotReview: {
      backgroundColor: PLAYBOOK_TONES.review.color,
    },
    filters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 4,
    },
    /**
     * The bar reads as fixed chrome through elevation rather than a tinted
     * wash: a status color here would compete with the rule chips it sits under
     * and turn ordinary progress into a warning.
     */
    footer: {
      alignItems: 'center',
      backgroundColor: theme.palette.common.white,
      borderTop: hairline,
      boxShadow: `0 -2px 6px ${alpha(theme.palette.common.black, 0.06)}`,
      display: 'flex',
      flexShrink: 0,
      justifyContent: 'space-between',
      minHeight: 48,
      padding: theme.spacing(1, 2),
    },
    footerButton: {
      '&:disabled': {
        cursor: 'default',
      },
      '&:hover:not(:disabled)': {
        backgroundColor: theme.appColors.darkBackground,
      },
      alignItems: 'center',
      background: 'none',
      border: 'none',
      borderRadius: 999,
      color: theme.appColors.secondaryType,
      cursor: 'pointer',
      display: 'flex',
      fontSize: 12,
      fontWeight: 600,
      gap: theme.spacing(0.25),
      padding: theme.spacing(0.5, 1),
    },
    footerButtonDisabled: {
      color: getPlaybookStrongBorder(theme),
      cursor: 'default',
    },
    footerComplete: {
      alignItems: 'center',
      color: PLAYBOOK_TONES.passed.color,
      display: 'flex',
      fontSize: 12,
      fontWeight: 600,
      gap: theme.spacing(0.5),
    },
    footerCount: {
      alignItems: 'center',
      color: theme.appColors.iconsType,
      display: 'flex',
      fontSize: 12,
      fontVariantNumeric: 'tabular-nums',
      fontWeight: 600,
      gap: theme.spacing(0.75),
    },
    footerNavigation: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(0.5),
    },
    /** The queue's forward action carries the panel's primary button language. */
    footerNextButton: {
      '&:disabled': {
        backgroundColor: PLAYBOOK_TONES.muted.background,
        color: PLAYBOOK_TONES.muted.color,
        cursor: 'default',
      },
      alignItems: 'center',
      backgroundColor: theme.palette.primary.main,
      border: 'none',
      borderRadius: 999,
      color: theme.palette.common.white,
      cursor: 'pointer',
      display: 'flex',
      fontSize: 12,
      fontWeight: 600,
      gap: theme.spacing(0.25),
      padding: theme.spacing(0.5, 1.25),
    },
    headerActions: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      gap: theme.spacing(1),
      marginLeft: theme.spacing(1),
    },
    headerIconButton: {
      color: theme.appColors.iconsType,
    },
    menuIcon: {
      '&.MuiListItemIcon-root': {
        color: theme.appColors.iconsType,
        minWidth: 22,
      },
    },
    notFound: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      padding: theme.spacing(2),
    },
    rerunButton: {
      alignItems: 'center',
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${getPlaybookStrongBorder(theme)}`,
      borderRadius: 999,
      color: theme.appColors.iconsType,
      cursor: 'pointer',
      display: 'flex',
      fontSize: 14,
      fontWeight: 600,
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.75, 1.75),
      whiteSpace: 'nowrap',
    },
    runButton: {
      alignItems: 'center',
      backgroundColor: theme.palette.primary.main,
      border: 'none',
      borderRadius: 999,
      color: theme.palette.common.white,
      cursor: 'pointer',
      display: 'flex',
      fontSize: 14,
      fontWeight: 600,
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.75, 1.75),
      whiteSpace: 'nowrap',
    },
    skeletonCategory: {
      alignItems: 'center',
      borderBottom: hairline,
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'space-between',
      padding: theme.spacing(1.25, 0),
    },
    skeletonChip: {
      borderRadius: 999,
      flexShrink: 0,
    },
    title: {
      ...createClampStyles(TITLE_MAX_LINES),
      fontSize: 18,
      fontWeight: 700,
      minWidth: 0,
    },
    /**
     * The title wraps to `TITLE_MAX_LINES`, so the options button aligns to
     * the first line rather than the middle of a grown block.
     */
    titleRow: {
      alignItems: 'flex-start',
      display: 'flex',
      gap: theme.spacing(0.5),
      justifyContent: 'space-between',
      paddingTop: theme.spacing(2),
    },
    viewIconButton: {
      color: theme.appColors.secondaryType,
      flexShrink: 0,
      padding: theme.spacing(0.5),
    },
  };
});

type FilterTone = Exclude<PlaybookRuleFilter, 'all'> | 'neutral';

interface FilterOption {
  count: number;
  filter: PlaybookRuleFilter;
  label: string;
  tone: FilterTone;
}

function getFilterOptions(counts: PlaybookRuleFilterCounts): FilterOption[] {
  return [
    { count: counts.all, filter: 'all', label: 'All', tone: 'neutral' },
    { count: counts.review, filter: 'review', ...PLAYBOOK_STATUS_CHIPS.review },
    { count: counts.resolved, filter: 'resolved', ...PLAYBOOK_STATUS_CHIPS.resolved },
    { count: counts.passed, filter: 'passed', ...PLAYBOOK_STATUS_CHIPS.passed },
  ];
}

function PlaybookDetailChrome({
  children,
  onBack,
}: {
  onBack: () => void;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <PanelHeader
      showBackButton
      backButtonTooltip="Back to playbooks"
      text="Playbooks"
      onClickBack={onBack}
    >
      {children}
    </PanelHeader>
  );
}

const DETAIL_SKELETON_FILTER_WIDTHS = [48, 108, 88] as const;
const DETAIL_SKELETON_CATEGORIES = [
  { chipWidth: 72, titleWidth: 168 },
  { chipWidth: 88, titleWidth: 132 },
  { chipWidth: 72, titleWidth: 196 },
  { chipWidth: 64, titleWidth: 148 },
] as const;

/**
 * Mirrors the loaded detail: title, outcome chips, collapsed category rows.
 * The header chrome is rendered by the caller so Back stays interactive.
 */
function PlaybookDetailSkeleton(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div aria-busy aria-label="Loading playbook" className={classes.container}>
      <div className={classes.titleRow}>
        <Skeleton animation="wave" height={28} variant="rounded" width="60%" />
        <Skeleton animation="wave" height={24} variant="circular" width={24} />
      </div>
      <div className={classes.filters}>
        {DETAIL_SKELETON_FILTER_WIDTHS.map(width => (
          <Skeleton
            key={width}
            animation="wave"
            className={classes.skeletonChip}
            height={26}
            variant="rounded"
            width={width}
          />
        ))}
      </div>
      <div className={classes.cards}>
        {DETAIL_SKELETON_CATEGORIES.map(category => (
          <div key={category.titleWidth} className={classes.skeletonCategory}>
            <Skeleton animation="wave" height={20} variant="rounded" width={category.titleWidth} />
            <Skeleton
              animation="wave"
              className={classes.skeletonChip}
              height={22}
              variant="rounded"
              width={category.chipWidth}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function PlaybookRunChromeButton({
  isRunnable,
  isRunning,
  onCancel,
  onRun,
  starting,
}: {
  isRunnable: boolean;
  isRunning: boolean;
  onCancel: () => void;
  onRun: () => void;
  starting: boolean;
}): JSX.Element {
  const { classes } = useStyles();

  if (isRunning)
    return (
      <button
        aria-label="Stop playbook"
        className={classes.rerunButton}
        type="button"
        onClick={onCancel}
      >
        <CircleStop size={14} /> Stop
      </button>
    );

  if (isRunnable)
    return (
      <button
        aria-label="Run playbook"
        className={classes.runButton}
        data-cy={Select.document.editor.rightDrawer.ai.panel.playbookDetail.runAllButton}
        disabled={starting}
        type="button"
        onClick={onRun}
      >
        <Play size={14} /> Run
      </button>
    );

  return (
    <button
      aria-label="Rerun playbook"
      className={classes.rerunButton}
      disabled={starting}
      type="button"
      onClick={onRun}
    >
      <RefreshCw size={14} /> Rerun
    </button>
  );
}

function ReviewQueueFooter({
  hasNext,
  hasPrevious,
  onNext,
  onPrevious,
  onViewAll,
  remainingCount,
}: {
  hasNext: boolean;
  hasPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onViewAll: () => void;
  remainingCount: number;
}): JSX.Element {
  const { classes, cx } = useStyles();
  if (!remainingCount) {
    return (
      <div className={classes.footer}>
        <span className={classes.footerComplete}>
          <Check size={14} /> Review Complete
        </span>
        <button className={classes.footerButton} type="button" onClick={onViewAll}>
          View all results
        </button>
      </div>
    );
  }

  return (
    <div className={classes.footer}>
      <span className={classes.footerCount}>
        <span className={cx(classes.filterDot, classes.filterDotReview)} />
        {remainingCount} rule{remainingCount === 1 ? '' : 's'} left
      </span>
      <div className={classes.footerNavigation}>
        <button
          className={cx(classes.footerButton, !hasPrevious && classes.footerButtonDisabled)}
          disabled={!hasPrevious}
          type="button"
          onClick={onPrevious}
        >
          <ArrowLeft size={14} /> Previous
        </button>
        <button
          className={classes.footerNextButton}
          disabled={!hasNext}
          type="button"
          onClick={onNext}
        >
          Next <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

export function PlaybookDetailPanel({
  documentId,
  onBack,
  playbookId,
}: {
  documentId: UUID;
  onBack: () => void;
  playbookId: UUID;
}): JSX.Element {
  const { classes, cx } = useStyles();
  const editorLoading = useEditorLoading();
  const editor = useMaybeEditorForDocument(documentId);
  const { document: currentDocument } = useGetCurrentDoc(documentId);
  const isAlineDocument = currentDocument?.latestVersion.__typename === 'DocumentVersion';
  const { docContentChangedCount } = useDocContentChangedCount();
  const { loading, playbook } = usePlaybookRun({ documentId, playbookId });
  const runActions = usePlaybookRunActions({ documentId, playbookId });
  const { acceptSuggestion } = usePlaybookMaterialize({ documentId });

  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(new Set());
  const [activeFilter, setActiveFilter] = useState<PlaybookRuleFilter>('all');
  const [activeReviewRuleId, setActiveReviewRuleId] = useState<string | null>(null);
  const [reviewSnapshotRuleIds, setReviewSnapshotRuleIds] = useState<string[]>([]);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const viewMenuButtonRef = useRef<HTMLButtonElement | null>(null);

  const categories = useMemo(() => playbook?.categories ?? [], [playbook]);
  const status = getPlaybookStatus(categories);
  /**
   * Latch for the playbook-wide Run/Rerun this panel started. Status
   * `running` is not enough on its own: auto-rerun puts single rules back
   * in flight all through a review. Set on click; drop it during render
   * once that run is no longer starting or streaming.
   */
  const [isFullRunInFlight, setIsFullRunInFlight] = useState(false);
  if (isFullRunInFlight && !runActions.starting && status !== 'running')
    setIsFullRunInFlight(false);

  const filterCounts = getRuleFilterCounts(categories);
  const reviewTargets = useMemo(() => getReviewTargets(categories), [categories]);
  const allCategoryIds = useMemo(
    () => new Set(categories.map(category => category.id)),
    [categories]
  );
  const reviewRuleIdSet = reviewTargets.ruleIds;
  const ruleCategoryIdById = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of categories)
      for (const rule of category.rules) map.set(rule.id, category.id);
    return map;
  }, [categories]);

  const visibleCategories = useMemo(() => {
    const reviewSnapshot = new Set(reviewSnapshotRuleIds);
    return categories
      .map(category => {
        const displayRules = category.rules
          .map((rule, index) => ({ rule, ruleNumber: index + 1 }))
          .filter(({ rule }) =>
            activeFilter === 'review'
              ? reviewSnapshot.has(rule.id)
              : getIsRuleInFilter(rule, activeFilter)
          );
        return { category, displayRules };
      })
      .filter(({ displayRules }) => displayRules.length > 0);
  }, [activeFilter, categories, reviewSnapshotRuleIds]);

  /**
   * Rebuilt on content ticks. Skip extraction while Yjs is still loading: the
   * editor exists immediately with placeholder children whose ids are not the
   * run's anchors.
   */
  const anchorInfoByKey = useMemo(() => {
    void docContentChangedCount;
    if (!editor || editorLoading) return new Map<string, AnchorInfo>();
    return getLiveAnchorInfoByKey({ docParts: editor.children, documentId, isAlineDocument });
  }, [docContentChangedCount, documentId, editor, editorLoading, isAlineDocument]);

  const documentReadyForStaleness = getIsLiveDocumentReadyToJudgeStaleness({
    hasEditor: Boolean(editor),
    isEditorLoading: editorLoading,
    liveAnchorCount: anchorInfoByKey.size,
  });
  /**
   * Arm one paint after the document is ready so a just-connected editor
   * cannot flash the rerun banner on the same frame its placeholder children
   * are replaced.
   */
  const [stalenessArmed, setStalenessArmed] = useState(false);
  useEffect(() => {
    if (!documentReadyForStaleness) {
      setStalenessArmed(false);
      return;
    }
    setStalenessArmed(true);
  }, [documentReadyForStaleness]);

  /**
   * Without a ready live document (chat drill-in, Yjs still loading, or
   * placeholder children) staleness is unknown rather than certain. Report
   * nothing stale; accepting is already refused until an editor is ready.
   */
  const getIsStale = useCallback(
    (previewCards: CodeBlockRedlineItem[]) => {
      if (!stalenessArmed) return false;
      return getIsSuggestionStale(previewCards, anchorInfoByKey);
    },
    [anchorInfoByKey, stalenessArmed]
  );

  /**
   * Accepts whose review mutation is still in flight. An accept lands in the
   * document first and records its decision second, so during the round trip
   * the run data still reads the suggestion as undecided while its own edit
   * has already changed the document — without this overlay the position
   * would flash its stale banner, and auto-rerun could destroy the review.
   */
  const [pendingAcceptedSuggestionIds, setPendingAcceptedSuggestionIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const addPendingAccepted = useCallback((suggestionId: string) => {
    setPendingAcceptedSuggestionIds(current => new Set(current).add(suggestionId));
  }, []);
  const removePendingAccepted = useCallback((suggestionId: string) => {
    setPendingAcceptedSuggestionIds(current => {
      const next = new Set(current);
      next.delete(suggestionId);
      return next;
    });
  }, []);

  usePlaybookAutoRerun({
    anchorInfoByKey,
    categories,
    editorLoading,
    pendingAcceptedSuggestionIds,
    rerunRules: runActions.rerunRules,
  });

  const scrollToRule = (ruleId: string) => {
    const categoryId = ruleCategoryIdById.get(ruleId);
    if (categoryId) setExpandedCategoryIds(current => new Set(current).add(categoryId));
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document
          .getElementById(getPlaybookRuleElementId(ruleId))
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  /**
   * Picking an outcome narrows the list to a handful of rules, so those
   * categories open to show them. Clicking the active outcome chip again
   * turns it off and returns to All. All is the read-through view and
   * stays collapsed, the same way the panel opens.
   */
  const applyFilter = (filter: PlaybookRuleFilter) => {
    const next = filter !== 'all' && activeFilter === filter ? 'all' : filter;
    setActiveFilter(next);
    setActiveReviewRuleId(null);
    switch (next) {
      case 'all':
        setExpandedCategoryIds(new Set());
        return;
      case 'review':
        /**
         * Leave the cursor unset so Next starts at the first snapshot rule
         * rather than skipping it as already visited.
         */
        setReviewSnapshotRuleIds([...reviewTargets.ruleIds]);
        setExpandedCategoryIds(new Set(reviewTargets.categoryIds));
        return;
      case 'passed':
      case 'resolved': {
        const categoryIds = categories
          .filter(category => category.rules.some(rule => getIsRuleInFilter(rule, next)))
          .map(category => category.id);
        setExpandedCategoryIds(new Set(categoryIds));
      }
    }
  };

  /**
   * A playbook run streams every rule. Drop the outcome filter so cards
   * don't appear and vanish as their status changes, and collapse so the
   * list reads as a fresh run — the same All view the panel opens with.
   */
  const lockFilterToAllForRun = () => {
    setActiveFilter('all');
    setActiveReviewRuleId(null);
    setReviewSnapshotRuleIds([]);
    setExpandedCategoryIds(new Set());
  };

  const startPlaybookRun = () => {
    lockFilterToAllForRun();
    setIsFullRunInFlight(true);
    void runActions.runPlaybook();
  };

  const toggleIn = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const previousReviewRuleId = getAdjacentReviewQueueRuleId({
    activeRuleId: activeReviewRuleId,
    direction: 'previous',
    liveRuleIds: reviewRuleIdSet,
    snapshotRuleIds: reviewSnapshotRuleIds,
  });
  const nextReviewRuleId = getAdjacentReviewQueueRuleId({
    activeRuleId: activeReviewRuleId,
    direction: 'next',
    liveRuleIds: reviewRuleIdSet,
    snapshotRuleIds: reviewSnapshotRuleIds,
  });
  const navigateReviewQueue = (ruleId: string | null) => {
    if (!ruleId) return;
    setActiveReviewRuleId(ruleId);
    scrollToRule(ruleId);
  };

  const dispatchStaleSnack = useDispatchInfoSnack({
    message: 'The document has changed since this run — rerun the rule to refresh its suggestions.',
  });
  const dispatchEditorNotReadySnack = useDispatchInfoSnack({
    message: 'The document editor is still loading — try again in a moment.',
  });
  const dispatchMaterializeFailedSnack = useDispatchInfoSnack({
    message: 'Could not apply that suggestion to the document. Try again or rerun the rule.',
  });
  const dispatchSigningLockedSnack = useDispatchInfoSnack({
    message: 'This document has been sent for signature, so suggestions can no longer be applied.',
  });
  const dispatchReviewFailedSnack = useDispatchInfoSnack({
    message:
      'The edit is in the document and remains marked accepted, but the review was not saved.',
  });

  /**
   * Two bursts from the panel's side of the screen when an action resolves the
   * last unresolved reviewable rule. Judged against the target set as it stood
   * before the mutation, and only for the rule that just completed.
   */
  const fireConfettiIfLastResolved = useCallback(
    (resolvedRuleId: string) => {
      if (reviewTargets.ruleIds.size !== 1) return;
      if (!reviewTargets.ruleIds.has(resolvedRuleId)) return;
      for (const delay of [0, 150]) {
        window.setTimeout(() => {
          void confetti({
            disableForReducedMotion: true,
            origin: { x: 0.85, y: 0.6 },
            particleCount: 80,
            spread: 70,
          });
        }, delay);
      }
    },
    [reviewTargets]
  );

  /**
   * The review completes server-side (the last decision auto-resolves the
   * rule), so completion is read off the mutation's response rather than
   * recomputed here.
   */
  const fireConfettiIfReviewCompleted = useCallback(
    (
      result: { data?: Apollo.ReviewPlaybookSuggestionMutation | null } | null,
      resolvedRuleId: string
    ) => {
      if (result?.data?.reviewPlaybookSuggestion.resolution)
        fireConfettiIfLastResolved(resolvedRuleId);
    },
    [fireConfettiIfLastResolved]
  );

  const ruleActions: PlaybookRuleActions = useMemo(
    () => ({
      clearResolution: jobId => runActions.clearResolution(jobId),
      rerunRule: ruleId => runActions.rerunRule(ruleId),
      resolve: async ({ jobId, kind, positionId }) => {
        const resolved = await runActions.resolveRule({ jobId, kind, positionId });
        const ruleId = getRuleIdForJob(categories, jobId);
        if (ruleId) fireConfettiIfLastResolved(ruleId);
        return resolved;
      },
      reviewSuggestion: async ({ jobId, suggestion, verb }) => {
        const ruleId = getRuleIdForJob(categories, jobId);
        // Accept lands in the document first; the mutation records what is
        // already there. Reject and un-reject never touch the document.
        if (verb === 'accept') {
          const result = await reviewAcceptedSuggestion({
            addPendingAccepted: () => addPendingAccepted(suggestion.id),
            materialize: () => acceptSuggestion({ suggestion }),
            onEditorNotReady: dispatchEditorNotReadySnack,
            onMaterializeFailed: dispatchMaterializeFailedSnack,
            onReviewFailed: dispatchReviewFailedSnack,
            onSigningLocked: dispatchSigningLockedSnack,
            onStale: dispatchStaleSnack,
            recordReview: () =>
              runActions.reviewSuggestion({
                jobId,
                suggestionId: suggestion.id,
                verb,
              }),
            removePendingAccepted: () => removePendingAccepted(suggestion.id),
          });
          if (ruleId && result) fireConfettiIfReviewCompleted(result, ruleId);

          return result;
        }
        const result = await runActions.reviewSuggestion({
          jobId,
          suggestionId: suggestion.id,
          verb,
        });
        if (ruleId) fireConfettiIfReviewCompleted(result, ruleId);
        return result;
      },
    }),
    [
      acceptSuggestion,
      addPendingAccepted,
      categories,
      dispatchEditorNotReadySnack,
      dispatchMaterializeFailedSnack,
      dispatchReviewFailedSnack,
      dispatchSigningLockedSnack,
      dispatchStaleSnack,
      fireConfettiIfLastResolved,
      fireConfettiIfReviewCompleted,
      removePendingAccepted,
      runActions,
    ]
  );

  if (loading && !playbook) {
    return (
      <>
        <PlaybookDetailChrome onBack={onBack}>
          <Spacer />
          <div className={classes.headerActions}>
            <Skeleton animation="wave" height={28} variant="circular" width={28} />
            <Skeleton
              animation="wave"
              className={classes.skeletonChip}
              height={32}
              variant="rounded"
              width={72}
            />
          </div>
        </PlaybookDetailChrome>
        <PlaybookDetailSkeleton />
      </>
    );
  }
  if (!playbook) {
    return (
      <>
        <PlaybookDetailChrome onBack={onBack} />
        <div className={classes.notFound}>
          <Typography>Playbook not found.</Typography>
        </div>
      </>
    );
  }

  const isRunnable = status === 'not-run';
  const isRunning = status === 'running';
  const showOutcomeFilters = getShowPlaybookOutcomeFilters({ isFullRunInFlight, status });
  const allCategoriesExpanded = [...allCategoryIds].every(id => expandedCategoryIds.has(id));
  const allCategoriesCollapsed = ![...allCategoryIds].some(id => expandedCategoryIds.has(id));
  const editHref = link.PlaybooksDetail({ playbookId });

  return (
    <>
      <PlaybookDetailChrome onBack={onBack}>
        <Spacer />
        <div className={classes.headerActions}>
          <AlineTooltip arrow placement="bottom" title="Edit playbook">
            <IconButton
              aria-label="Edit playbook"
              className={classes.headerIconButton}
              component={RouterLink}
              size="small"
              to={editHref}
              onClick={event =>
                openLinkFromAnchorClick(event, editHref, {
                  newTab: true,
                  title: playbook.title || 'Untitled playbook',
                })
              }
            >
              <Pencil size={16} />
            </IconButton>
          </AlineTooltip>
          <PlaybookRunChromeButton
            isRunnable={isRunnable}
            isRunning={isRunning}
            starting={runActions.starting}
            onCancel={() => void runActions.cancelRun()}
            onRun={startPlaybookRun}
          />
        </div>
      </PlaybookDetailChrome>
      <div className={classes.container}>
        <div className={classes.titleRow}>
          <Typography className={classes.title} title={playbook.title}>
            {playbook.title}
          </Typography>
          <AlineTooltip arrow placement="left" title="Playbook options">
            <IconButton
              ref={viewMenuButtonRef}
              aria-label="Playbook options"
              className={classes.viewIconButton}
              size="small"
              onClick={() => setViewMenuOpen(true)}
            >
              <MoreHorizontal size={16} />
            </IconButton>
          </AlineTooltip>
        </div>
        {showOutcomeFilters && (
          <div className={classes.filters}>
            {getFilterOptions(filterCounts).map(option => (
              <button
                key={option.filter}
                aria-pressed={activeFilter === option.filter}
                className={cx(
                  classes.filter,
                  activeFilter === option.filter && classes.filterActive
                )}
                type="button"
                onClick={() => applyFilter(option.filter)}
              >
                {option.tone !== 'neutral' && (
                  <span
                    className={classes.filterDot}
                    style={{ backgroundColor: PLAYBOOK_TONE_COLORS[option.tone].color }}
                  />
                )}
                {option.label}
                <span className={classes.filterCount}>{option.count}</span>
              </button>
            ))}
          </div>
        )}
        <Menu
          anchorEl={viewMenuButtonRef.current}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          open={viewMenuOpen}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          onClose={() => setViewMenuOpen(false)}
        >
          <MenuItem
            dense
            disabled={allCategoriesExpanded}
            onClick={() => {
              setViewMenuOpen(false);
              setExpandedCategoryIds(new Set(allCategoryIds));
            }}
          >
            <ListItemIcon className={classes.menuIcon}>
              <ChevronsUpDown size={MENU_ICON_SIZE} />
            </ListItemIcon>
            <ListItemText>Expand all</ListItemText>
          </MenuItem>
          <MenuItem
            dense
            disabled={allCategoriesCollapsed}
            onClick={() => {
              setViewMenuOpen(false);
              setExpandedCategoryIds(new Set());
            }}
          >
            <ListItemIcon className={classes.menuIcon}>
              <ChevronsDownUp size={MENU_ICON_SIZE} />
            </ListItemIcon>
            <ListItemText>Collapse all</ListItemText>
          </MenuItem>
        </Menu>
        <div className={classes.cards}>
          {visibleCategories.map(({ category, displayRules }) => (
            <PlaybookCategoryCard
              key={category.id}
              actions={ruleActions}
              category={category}
              displayRules={displayRules}
              expanded={expandedCategoryIds.has(category.id)}
              getIsStale={getIsStale}
              pendingAcceptedSuggestionIds={pendingAcceptedSuggestionIds}
              playbookId={playbookId}
              onToggleExpanded={() =>
                setExpandedCategoryIds(current => toggleIn(current, category.id))
              }
            />
          ))}
          {visibleCategories.length === 0 && (
            <Typography className={classes.emptyFilter}>No rules match this filter.</Typography>
          )}
        </div>
      </div>
      {showOutcomeFilters && activeFilter === 'review' && (
        <ReviewQueueFooter
          hasNext={nextReviewRuleId !== null}
          hasPrevious={previousReviewRuleId !== null}
          remainingCount={reviewTargets.ruleIds.size}
          onNext={() => navigateReviewQueue(nextReviewRuleId)}
          onPrevious={() => navigateReviewQueue(previousReviewRuleId)}
          onViewAll={() => applyFilter('all')}
        />
      )}
    </>
  );
}

function getRuleIdForJob(categories: PlaybookCategoryLike[], jobId: string): string | undefined {
  for (const category of categories)
    for (const rule of category.rules) if (getRuleJob(rule)?.id === jobId) return rule.id;
}
